import React from 'react';
import { useNavigate } from 'react-router-dom';
import cn from '@/utils/cn';
import SPAButton from '../shared/SPAButton';
import Findings, { ranked } from './Findings';
import type { Finding } from './Findings';
import useAssignees from './useAssignees';
import TeacherMessage, { messagesFor } from './TeacherMessage';
import type { AssignmentSummary } from './useAssignmentSyncState';
import SPASectionHeading from '../shared/SPASectionHeading';

// AIDEV-NOTE: Almost every case is one of three repairs — unassign the classes the LMS course does not
// hold, link a never-launched assignment to its column, or move a column's link to the right
// assignment — so the page leads with whichever applies and the button that does it. Order is the
// preference when several fire. A recovered deep link comes first: it names the course, which beats the
// DB-only guess that a sibling is blocking (that finding stays listed underneath).
const FIXES: { code: string; label: string; to: string }[] = [
  { code: 'deep_link_candidate', label: 'Connect it to its gradebook column', to: 'columns' },
  { code: 'unlinked_blocked', label: 'Move the gradebook link here', to: 'columns' },
  { code: 'shared_line_item', label: 'Choose which copy keeps the gradebook column', to: 'columns' },
  { code: 'unlinked', label: 'Find its gradebook column', to: 'columns' },
  { code: 'classes_without_a_course', label: 'Find its column in the other courses', to: 'columns' },
  { code: 'classes_covered_by_twins', label: 'Review classes to remove', to: 'assignees' },
];

/** Findings that say the same thing as the chosen one, and would only repeat it underneath. */
const SAME_FAULT: Record<string, string[]> = {
  classes_covered_by_twins: ['duplicate_content'],
  deep_link_candidate: ['unlinked'],
  unlinked_blocked: ['duplicate_content'],
};

const RemovalOutlook = ({ assignmentId }: { assignmentId: string }) => {
  const { preview } = useAssignees(assignmentId);
  const drops = preview.data?.classrooms ?? [];

  if (drops.length === 0) return null;

  const safe = drops.filter(d => d.removable).length;

  return (
    <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-spa-primary">
      {preview.data?.removable
        ? `${safe} of ${drops.length} can come off without stranding anyone's score.`
        : preview.data?.blocked_reason}
    </p>
  );
};

interface FixCardProps {
  assignment: AssignmentSummary;
  assignmentId: string;
  findings: Finding[];
  /** Assigned students with no LMS identity — the teacher has to get them to launch once. */
  neverLaunched: number;
}

const FixCard = ({
  assignment, assignmentId, findings, neverLaunched,
}: FixCardProps) => {
  const navigate = useNavigate();
  // A recovered deep link means support can link it; asking the teacher to open it would be busywork.
  const recovered = findings.some(f => f.code === 'deep_link_candidate');
  const theirs = recovered ? findings.filter(f => f.code !== 'unlinked') : findings;
  const messages = messagesFor(theirs, { assignment, neverLaunched });

  if (findings.length === 0) {
    return (
      <div className="tw-mb-6">
        <Findings findings={[]} />
        <TeacherMessage messages={messages} />
      </div>
    );
  }

  const fix = FIXES.find(f => findings.some(finding => finding.code === f.code));
  const lead = fix == null ? ranked(findings)[0] : findings.find(f => f.code === fix.code) as Finding;
  const hidden = [lead.code, ...(SAME_FAULT[lead.code] ?? [])];
  const rest = findings.filter(f => !hidden.includes(f.code));
  const error = findings.some(f => f.severity === 'error');

  return (
    <div className="tw-mb-6">
      <section
        className={cn(
          'tw-rounded tw-border tw-border-l-4 tw-border-solid tw-bg-spa-bg-secondary tw-px-4 tw-py-3',
          'tw-border-spa-border',
          error ? 'tw-border-l-spa-danger-solid' : 'tw-border-l-spa-warning-solid',
        )}
      >
        <h3 className="tw-m-0 tw-text-base tw-font-semibold tw-text-spa-heading">{lead.title}</h3>
        <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-spa-secondary">{lead.detail}</p>
        {fix?.to === 'assignees' && <RemovalOutlook assignmentId={assignmentId} />}
        {lead.owner === 'lms' && fix == null && (
          <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-spa-primary">
            Only the teacher can fix this, in the LMS — there is nothing to change on our side.
          </p>
        )}
        {fix != null && (
          <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-2">
            <SPAButton type="button" variant="primary" onClick={() => navigate(fix.to)}>
              {fix.label}
            </SPAButton>
            {/* A course it lacks usually has its column already, but some classes may not use it at all. */}
            {lead.code === 'classes_without_a_course' && (
              <SPAButton type="button" variant="secondary" onClick={() => navigate('assignees')}>
                Review classes to remove
              </SPAButton>
            )}
          </div>
        )}
        <TeacherMessage messages={messages} />
      </section>

      {rest.length > 0 && (
        <div className="tw-mt-3">
          <SPASectionHeading title="Also found" />
          <Findings findings={rest} />
        </div>
      )}
    </div>
  );
};

export default FixCard;
